import "./ScheduleCheckInDrawer.css";
import "./EditAppointmentDrawer.css";
import "antd/dist/reset.css";
import { FaCalendarAlt, FaClock, FaPhone, FaVideo } from "react-icons/fa";
import { TimePicker } from "antd";
import { toast } from "react-toastify";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";

const DURATION_OPTIONS = [
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "60 minutes", value: 60 },
];

const formatDisplayDate = (dateValue) => {
  if (!dateValue) return "";
  return dayjs(dateValue).format("MM/DD/YYYY");
};

const EditAppointmentDrawer = ({
  appointment,
  fetchAppointmentData,
  onCancel,
  onSaved,
}) => {
  const user = useSelector((state) => state?.user);
  const dateInputRef = useRef(null);

  const patient = appointment?.user;
  const patientName = `${patient?.firstname ?? ""} ${
    patient?.lastname ?? ""
  }`.trim();

  const [appointmentMethod, setAppointmentMethod] = useState("video");
  const [date, setDate] = useState("");
  const [duration, setDuration] = useState("");
  const [initialForm, setInitialForm] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [time, setTime] = useState("");
  const [timeValue, setTimeValue] = useState(null);

  useEffect(() => {
    if (!appointment) return;

    const appointmentTime = dayjs(appointment.appointmenttime);
    const formState = {
      appointmentMethod: appointment.appointmentMethod || "video",
      date: appointmentTime.format("YYYY-MM-DD"),
      duration: String(appointment.sessionLength || 30),
      time: appointmentTime.format("HH:mm"),
    };

    setInitialForm(formState);
    setAppointmentMethod(formState.appointmentMethod);
    setDate(formState.date);
    setDuration(formState.duration);
    setTime(formState.time);
    setTimeValue(appointmentTime);
    setShowSaveConfirmModal(false);
  }, [appointment?._id, appointment]);

  useEffect(() => {
    const forceZIndex = () => {
      document.querySelectorAll(".ant-picker-dropdown").forEach((popup) => {
        popup.style.zIndex = "10002";
      });
    };

    forceZIndex();
    const observer = new MutationObserver(forceZIndex);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const hasChanges = useMemo(() => {
    if (!initialForm) return false;

    return (
      date !== initialForm.date ||
      time !== initialForm.time ||
      duration !== initialForm.duration ||
      appointmentMethod !== initialForm.appointmentMethod
    );
  }, [appointmentMethod, date, duration, initialForm, time]);

  const saveChanges = async () => {
    if (!date) {
      toast.error("Please select a date.");
      return false;
    }

    if (!time) {
      toast.error("Please select a time.");
      return false;
    }

    if (!duration) {
      toast.error("Please select a duration.");
      return false;
    }

    if (!hasChanges) {
      return true;
    }

    const authToken = user.user?.token;
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    setIsLoading(true);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update/${appointment._id}`,
        {
          appointmentId: appointment._id,
          appointmentMethod,
          date,
          dbtime: time,
          duration: Number(duration),
          selectedProvider: appointment.specialistid,
          selectedUser: appointment.user_fk,
          timeZone: clientTimeZone,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Check-in updated successfully.");
        fetchAppointmentData?.();
        return true;
      }

      toast.error(response.data.message || "Could not update the check-in.");
      return false;
    } catch (error) {
      console.error("EditAppointmentDrawer submit error:", error);
      toast.error(
        error.response?.data?.message || "Failed to update check-in."
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const saved = await saveChanges();
    if (saved) {
      onSaved?.();
    }
  };

  const handleCloseAttempt = () => {
    if (hasChanges) {
      setShowSaveConfirmModal(true);
      return;
    }

    onCancel();
  };

  const handleDiscardChanges = () => {
    setShowSaveConfirmModal(false);
    onCancel();
  };

  const handleConfirmSave = async () => {
    const saved = await saveChanges();
    if (saved) {
      setShowSaveConfirmModal(false);
      onSaved?.();
    }
  };

  if (!appointment) return null;

  return (
    <>
      <div
        className="cm-schedule-drawer-overlay cm-edit-drawer-overlay"
        onClick={handleCloseAttempt}
        role="presentation">
        <aside
          aria-label="Edit check-in"
          className="cm-schedule-drawer cm-edit-checkin-drawer"
          onClick={(event) => event.stopPropagation()}>
          <div className="cm-schedule-drawer-header">
            <h2 className="cm-schedule-drawer-title">Edit Check-in</h2>
            <button
              className="cm-schedule-drawer-close"
              onClick={handleCloseAttempt}
              type="button">
              Close
            </button>
          </div>

          <form className="cm-schedule-drawer-form" onSubmit={handleSubmit}>
            <div className="cm-schedule-drawer-body">
              <div className="cm-schedule-field">
                <label htmlFor="edit-checkin-patient">Patient</label>
                <div
                  className="cm-edit-patient-readonly"
                  id="edit-checkin-patient">
                  {patientName || "Unknown Patient"}
                </div>
              </div>

              <div className="cm-schedule-field">
                <label htmlFor="edit-checkin-date">Date</label>
                <div
                  className={`cm-schedule-input-shell${
                    date ? "" : " is-placeholder"
                  }`}
                  onClick={() => {
                    try {
                      dateInputRef.current?.showPicker();
                    } catch {
                      dateInputRef.current?.focus();
                    }
                  }}>
                  <input
                    ref={dateInputRef}
                    className="cm-schedule-native-input"
                    id="edit-checkin-date"
                    onChange={(event) => setDate(event.target.value)}
                    type="date"
                    value={date}
                  />
                  <span>{date ? formatDisplayDate(date) : "Select date"}</span>
                  <FaCalendarAlt className="cm-schedule-field-icon" />
                </div>
              </div>

              <div className="cm-schedule-field">
                <label htmlFor="edit-checkin-time">Time</label>
                <TimePicker
                  className="cm-schedule-time-picker"
                  format="h:mm a"
                  hourStep={1}
                  id="edit-checkin-time"
                  minuteStep={15}
                  onChange={(value) => {
                    if (value) {
                      setTime(value.format("HH:mm"));
                      setTimeValue(value);
                    } else {
                      setTime("");
                      setTimeValue(null);
                    }
                  }}
                  placeholder="Select time"
                  suffixIcon={<FaClock className="cm-schedule-field-icon" />}
                  use12Hours
                  value={timeValue}
                />
              </div>

              <div className="cm-schedule-field">
                <label htmlFor="edit-checkin-duration">Duration</label>
                <select
                  className="cm-schedule-duration-select"
                  id="edit-checkin-duration"
                  onChange={(event) => setDuration(event.target.value)}
                  required
                  value={duration}>
                  <option disabled value="">
                    Select duration
                  </option>
                  {DURATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="cm-schedule-field">
                <label>Method</label>
                <div className="cm-schedule-method-toggle">
                  <button
                    className={`cm-schedule-method-btn${
                      appointmentMethod === "video" ? " is-active" : ""
                    }`}
                    onClick={() => setAppointmentMethod("video")}
                    type="button">
                    <FaVideo />
                    Video
                  </button>
                  <button
                    className={`cm-schedule-method-btn${
                      appointmentMethod === "phone" ? " is-active" : ""
                    }`}
                    onClick={() => setAppointmentMethod("phone")}
                    type="button">
                    <FaPhone />
                    Phone
                  </button>
                </div>
              </div>
            </div>

            <div className="cm-schedule-drawer-footer">
              <button
                className="cm-schedule-btn-cancel"
                disabled={isLoading}
                onClick={handleCloseAttempt}
                type="button">
                Cancel
              </button>
              <button
                className={`cm-schedule-btn-save${
                  hasChanges ? " is-active" : ""
                }`}
                disabled={!hasChanges || isLoading}
                type="submit">
                {isLoading ? "Scheduling..." : "Schedule"}
              </button>
            </div>
          </form>
        </aside>
      </div>

      {showSaveConfirmModal && (
        <div
          className="cm-edit-save-modal-overlay"
          onClick={() => setShowSaveConfirmModal(false)}
          role="presentation">
          <div
            aria-labelledby="cm-edit-save-modal-title"
            aria-modal="true"
            className="cm-edit-save-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog">
            <div className="cm-edit-save-modal-header">
              <h3 className="cm-edit-save-modal-title" id="cm-edit-save-modal-title">
                Save Changes?
              </h3>
              <button
                aria-label="Close dialog"
                className="cm-edit-save-modal-close"
                onClick={() => setShowSaveConfirmModal(false)}
                type="button">
                &times;
              </button>
            </div>

            <p className="cm-edit-save-modal-message">
              If you close this appointment without saving, all changes will be
              lost.
            </p>

            <div className="cm-edit-save-modal-footer">
              <button
                className="cm-edit-save-modal-btn-cancel"
                disabled={isLoading}
                onClick={handleDiscardChanges}
                type="button">
                Cancel
              </button>
              <button
                className="cm-edit-save-modal-btn-save"
                disabled={isLoading}
                onClick={handleConfirmSave}
                type="button">
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditAppointmentDrawer;
